import {
  Button,
  Col,
  Dropdown,
  Form,
  Popconfirm,
  Row,
  Space,
  Tooltip,
} from 'antd'
import CIMSSection from '../CIMSSection'
import { CIMSFormItemListProps } from './types'
import { DeleteTwoTone, PlusCircleTwoTone } from '@ant-design/icons'

function CIMSFormItemList<ItemType>({
  children,
  label: labelOrObject,
  help,
  name,
  validate,
  buttons,
  rowOptions,
  buttonRowExtras,
}: CIMSFormItemListProps<ItemType>) {
  let singular: string, plural: string
  if (typeof labelOrObject === 'string') {
    singular = labelOrObject
    plural = labelOrObject + 's'
  } else {
    singular = labelOrObject.singular
    plural = labelOrObject.plural
  }
  return (
    <CIMSSection title={plural} help={help}>
      <Form.List
        name={name}
        rules={
          validate
            ? [
                {
                  validator: async (_, value: ItemType[]) => {
                    const result = await validate(value)
                    if (result !== true) throw new Error(result)
                  },
                },
              ]
            : undefined
        }
      >
        {(fields, { add, remove }, { errors, warnings }) => (
          <>
            <Space style={{ marginBottom: '1rem' }}>
              {buttonRowExtras?.position === 'around' && buttonRowExtras.before}
              {buttonRowExtras?.position === 'before' &&
                buttonRowExtras.children}
              {buttons.reduce((renderedButtons, buttonOptions) => {
                const { loading, disabled, labelPrefix, ...button } =
                  buttonOptions
                if (
                  button.hideAfter !== undefined &&
                  fields.length > button.hideAfter
                )
                  return renderedButtons

                const icon =
                  button.icon === 'add' ? <PlusCircleTwoTone /> : button.icon
                const label = button.icon === 'add' ? singular : button.label
                const buttonLabel = `${labelPrefix ?? ''}${label}`
                const key = `${button.icon}-${buttonLabel}-${button.type}`
                if (button.type === 'dropdown') {
                  const { items } = button
                  const disable = loading || disabled || !items?.length
                  renderedButtons.push(
                    <Dropdown
                      key={key}
                      disabled={disable}
                      menu={{
                        items,
                        style: { maxHeight: 300, overflowY: 'auto' },
                        onClick: ({ key }) => {
                          const value = button.add(key)
                          if (value !== undefined) add(button.add(key))
                        },
                      }}
                    >
                      <Tooltip
                        title={
                          !items?.length
                            ? `There are no ${label} records to select`
                            : undefined
                        }
                      >
                        <Button
                          disabled={disable}
                          loading={loading}
                          icon={icon}
                          type="primary"
                        >
                          {buttonLabel}
                        </Button>
                      </Tooltip>
                    </Dropdown>
                  )

                  return renderedButtons
                }
                renderedButtons.push(
                  <Button
                    key={key}
                    type="primary"
                    loading={loading}
                    disabled={loading || disabled}
                    icon={icon}
                    onClick={() => add(button.add())}
                  >
                    {buttonLabel}
                  </Button>
                )
                return renderedButtons
              }, Array<JSX.Element>())}
              {buttonRowExtras?.position === 'around' && buttonRowExtras.after}
              {buttonRowExtras?.position === 'after' &&
                buttonRowExtras.children}
            </Space>
            {fields.map(({ key, name }) => (
              <Row key={key} wrap={false}>
                <Col flex="auto">
                  <Row
                    gutter={rowOptions?.gutter ?? 16}
                    justify={rowOptions?.justify}
                  >
                    {children(name)}
                  </Row>
                </Col>
                {fields.length > (rowOptions?.showDeleteAfter ?? 0) && (
                  <Col flex="none" style={{ alignSelf: 'center' }}>
                    <Form.Item>
                      <Popconfirm
                        title={`Are you sure you want to remove this ${singular}? This action cannot be undone!`}
                        onConfirm={() => remove(name)}
                      >
                        <Button icon={<DeleteTwoTone />} type="text" />
                      </Popconfirm>
                    </Form.Item>
                  </Col>
                )}
              </Row>
            ))}
            <Form.ErrorList errors={errors} warnings={warnings} />
          </>
        )}
      </Form.List>
    </CIMSSection>
  )
}

export default CIMSFormItemList
